import { useEffect, useState } from "react";
import type { ProjectContextValue } from "@/lib/project";
import type { Color, EpicListItem, SprintListItem, TaskListItem } from "@/lib/types";
import { totalSeconds } from "@/lib/utils";

export const NONE = "__none";

export interface Filter {
  field: string; // "stage" | "assignee" | "epic" | "sprint" | propertyId
  values: string[];
}

export interface ViewState {
  view: "board" | "table";
  search: string;
  groupBy: string; // "stage" | propertyId
  sort: { key: string; dir: "asc" | "desc" };
  filters: Filter[];
  mine: boolean;
  hideEmpty: boolean;
}

const defaults: ViewState = {
  view: "board",
  search: "",
  groupBy: "stage",
  sort: { key: "manual", dir: "asc" },
  filters: [],
  mine: false,
  hideEmpty: false,
};

/** View settings persisted per scope in localStorage (search is not persisted). */
export function useViewState(storageKey: string) {
  const [state, setState] = useState<ViewState>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? { ...defaults, ...JSON.parse(raw), search: "" } : defaults;
    } catch {
      return defaults;
    }
  });
  useEffect(() => {
    try {
      const { search: _search, ...rest } = state;
      localStorage.setItem(storageKey, JSON.stringify(rest));
    } catch {
      /* ignore */
    }
  }, [state, storageKey]);
  const update = (patch: Partial<ViewState>) => setState((s) => ({ ...s, ...patch }));
  return [state, update] as const;
}

export interface FieldDef {
  id: string;
  label: string;
  choices: { id: string; label: string; color?: Color }[];
}

export function filterFields(ctx: ProjectContextValue, opts: { epics?: EpicListItem[]; sprints?: SprintListItem[]; projectScope: boolean }): FieldDef[] {
  const fields: FieldDef[] = [
    { id: "stage", label: "Stage", choices: ctx.bundle.stages.map((s) => ({ id: s.id, label: s.name, color: s.color })) },
    {
      id: "assignee",
      label: "Assignee",
      choices: [{ id: NONE, label: "Unassigned" }, ...ctx.members.map((m) => ({ id: m.id, label: m.name }))],
    },
    {
      id: "assigneeStatus",
      label: "Assignee status",
      choices: ctx.bundle.assigneeStatuses.map((s) => ({ id: s.id, label: s.name, color: s.color })),
    },
  ];
  if (opts.projectScope) {
    fields.push({ id: "epic", label: "Epic", choices: (opts.epics ?? []).map((e) => ({ id: e.id, label: e.title })) });
    fields.push({
      id: "sprint",
      label: "Sprint",
      choices: [{ id: NONE, label: "Backlog (no sprint)" }, ...(opts.sprints ?? []).map((s) => ({ id: s.id, label: s.title }))],
    });
  }
  for (const p of ctx.propsFor("task")) {
    if (p.type === "date") continue;
    fields.push({ id: p.id, label: p.name, choices: [{ id: NONE, label: `No ${p.name}` }, ...p.options.map((o) => ({ id: o.id, label: o.label, color: o.color }))] });
  }
  return fields;
}

function matches(task: TaskListItem, f: Filter): boolean {
  if (!f.values.length) return true;
  const has = (v: string) => f.values.includes(v);
  switch (f.field) {
    case "stage":
      return has(task.stageId);
    case "assignee":
      return task.assignees.length === 0 ? has(NONE) : task.assignees.some((a) => has(a.userId));
    case "assigneeStatus":
      return task.assignees.some((a) => a.statusId && has(a.statusId));
    case "epic":
      return has(task.epicId);
    case "sprint":
      return has(task.sprintId ?? NONE);
    default: {
      const vals = task.values[f.field] ?? [];
      return vals.length === 0 ? has(NONE) : vals.some(has);
    }
  }
}

export function applyFilters(tasks: TaskListItem[], state: ViewState, meId: string) {
  const q = state.search.trim().toLowerCase().replace(/^[a-z][a-z0-9]*-(?=\d+$)/, "");
  return tasks.filter(
    (t) =>
      (!q || t.title.toLowerCase().includes(q) || String(t.number) === q) &&
      (!state.mine || t.assignees.some((a) => a.userId === meId)) &&
      state.filters.every((f) => matches(t, f)),
  );
}

export function sortFields(ctx: ProjectContextValue) {
  return [
    { id: "manual", label: "Manual order" },
    { id: "number", label: "Task number" },
    { id: "title", label: "Title" },
    { id: "stage", label: "Stage" },
    { id: "time", label: "Time on task" },
    { id: "created", label: "Created" },
    { id: "updated", label: "Last edited" },
    ...ctx.propsFor("task").map((p) => ({ id: p.id, label: p.name })),
  ];
}

export function applySort(tasks: TaskListItem[], state: ViewState, ctx: ProjectContextValue) {
  const { key, dir } = state.sort;
  const sign = dir === "asc" ? 1 : -1;
  const prop = ctx.bundle.properties.find((p) => p.id === key);
  const now = Date.now();
  const val = (t: TaskListItem): number | string | null => {
    switch (key) {
      case "manual":
        return t.position;
      case "number":
        return t.number;
      case "title":
        return t.title.toLowerCase();
      case "stage":
        return ctx.stageById.get(t.stageId)?.position ?? 0;
      case "time":
        return totalSeconds(t.time, now);
      case "created":
        return t.createdAt;
      case "updated":
        return t.updatedAt;
      default: {
        const v = t.values[key];
        if (!prop || !v?.length) return null;
        if (prop.type === "date") return v[0];
        return Math.min(...v.map((id) => ctx.optionById.get(id)?.position ?? 999));
      }
    }
  };
  return [...tasks].sort((a, b) => {
    const va = val(a);
    const vb = val(b);
    if (va === null && vb === null) return a.number - b.number;
    if (va === null) return 1; // empty values last regardless of direction
    if (vb === null) return -1;
    if (va < vb) return -sign;
    if (va > vb) return sign;
    return a.position - b.position || a.number - b.number;
  });
}

export interface Group {
  id: string;
  label: string;
  color: Color;
}

export function groupsFor(ctx: ProjectContextValue, groupBy: string): Group[] {
  if (groupBy === "stage") return ctx.bundle.stages.map((s) => ({ id: s.id, label: s.name, color: s.color }));
  const prop = ctx.bundle.properties.find((p) => p.id === groupBy);
  if (!prop) return ctx.bundle.stages.map((s) => ({ id: s.id, label: s.name, color: s.color }));
  return [{ id: NONE, label: `No ${prop.name}`, color: "gray" }, ...prop.options.map((o) => ({ id: o.id, label: o.label, color: o.color }))];
}

export function groupIdsOf(task: TaskListItem, groupBy: string, ctx: ProjectContextValue): string[] {
  if (groupBy === "stage" || !ctx.bundle.properties.some((p) => p.id === groupBy)) return [task.stageId];
  const v = task.values[groupBy] ?? [];
  return v.length ? v : [NONE];
}
